import { type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';

import { Announcements } from '@air/api';
import { callAirApi } from '@air/utils-api';

import {
  PRIVATE_ANNOUNCEMENTS_LIST_KEY,
  type PrivateAnnouncementsListData,
} from '~/components/v2/hooks/queries/usePrivateAnnouncementsList';

type DismissPrivateAnnouncementParams = NonNullable<Parameters<typeof Announcements.dismiss>[0]>;

export const usePrivateAnnouncementDismissMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    DismissPrivateAnnouncementParams,
    { previousLists: [QueryKey, PrivateAnnouncementsListData | undefined][] }
  >({
    mutationFn: async (params: DismissPrivateAnnouncementParams) => {
      await callAirApi(() => Announcements.dismiss(params));
    },
    onError: (_error, _variables, context) => {
      context?.previousLists.forEach(([queryKey, previousData]) => {
        queryClient.setQueryData<PrivateAnnouncementsListData | undefined>(queryKey, previousData);
      });
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [PRIVATE_ANNOUNCEMENTS_LIST_KEY] });

      const cachedLists = queryClient.getQueriesData<PrivateAnnouncementsListData>({
        queryKey: [PRIVATE_ANNOUNCEMENTS_LIST_KEY],
      });

      const previousLists: [QueryKey, PrivateAnnouncementsListData | undefined][] = [];

      for (const [queryKey, data] of cachedLists) {
        const hasAnnouncement = data?.some((announcement) => announcement.id === variables.announcementId);

        if (!data || !hasAnnouncement) {
          continue;
        }

        previousLists.push([queryKey, data]);
        queryClient.setQueryData<PrivateAnnouncementsListData>(
          queryKey,
          data.filter((announcement) => announcement.id !== variables.announcementId),
        );
      }

      return { previousLists };
    },
    onSettled: (_data, _error, _variables, context) => {
      context?.previousLists.forEach(([queryKey]) => {
        void queryClient.invalidateQueries({ queryKey });
      });
    },
  });
};
